import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CreditCard, Plus, Pencil, Trash2, Loader2, Search } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentMethodsPage = () => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    is_enabled: true,
    is_active: true
  });

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("gasman_token")}` }
  });

  const fetchPaymentMethods = async () => {
    try {
      const response = await axios.get(`${API}/payment-methods`, getAuthHeaders());
      setPaymentMethods(response.data);
    } catch (error) {
      toast.error("Failed to fetch payment methods");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const resetForm = () => {
    setFormData({
      name: "",
      is_enabled: true,
      is_active: true
    });
    setSelectedMethod(null);
  };

  const openAddDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (method) => {
    setSelectedMethod(method);
    setFormData({
      name: method.name,
      is_enabled: method.is_enabled,
      is_active: method.is_active
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (method) => {
    setSelectedMethod(method);
    setDeleteDialogOpen(true);
  };

  const handleToggleEnabled = async (method) => {
    try {
      await axios.put(`${API}/payment-methods/${method.id}`, { is_enabled: !method.is_enabled }, getAuthHeaders());
      toast.success(`Payment method ${!method.is_enabled ? 'enabled' : 'disabled'}`);
      fetchPaymentMethods();
    } catch (error) {
      toast.error("Failed to update payment method");
    }
  };

  const handleToggleActive = async (method) => {
    try {
      await axios.put(`${API}/payment-methods/${method.id}`, { is_active: !method.is_active }, getAuthHeaders());
      toast.success(`Payment method ${!method.is_active ? 'activated' : 'deactivated'}`);
      fetchPaymentMethods();
    } catch (error) {
      toast.error("Failed to update payment method");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (selectedMethod) {
        await axios.put(`${API}/payment-methods/${selectedMethod.id}`, formData, getAuthHeaders());
        toast.success("Payment method updated successfully");
      } else {
        await axios.post(`${API}/payment-methods`, formData, getAuthHeaders());
        toast.success("Payment method added successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchPaymentMethods();
    } catch (error) {
      const message = error.response?.data?.detail || "Something went wrong";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    try {
      await axios.delete(`${API}/payment-methods/${selectedMethod.id}`, getAuthHeaders());
      toast.success("Payment method deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedMethod(null);
      fetchPaymentMethods();
    } catch (error) {
      toast.error("Failed to delete payment method");
    } finally {
      setSubmitting(false);
    }
  };

  const filteredMethods = paymentMethods.filter(m =>
    m.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div data-testid="payment-methods-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <CreditCard className="text-[#0A4CAE]" size={28} />
            Payment Methods
          </h1>
          <p className="text-gray-600 mt-1">Manage available payment methods</p>
        </div>
        <Button
          onClick={openAddDialog}
          className="bg-[#0A4CAE] text-white hover:bg-[#F7851E] rounded-lg glow-primary"
          data-testid="add-payment-method-btn"
        >
          <Plus size={20} className="mr-2" />
          Add Payment Method
        </Button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <Input
            placeholder="Search payment methods..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white border-gray-300"
            data-testid="search-payment-methods"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-[#0A4CAE]" size={32} />
          </div>
        ) : filteredMethods.length === 0 ? (
          <div className="text-center py-12">
            <CreditCard className="mx-auto text-gray-300 mb-4" size={48} />
            <p className="text-gray-500">
              {searchTerm ? "No payment methods found matching your search" : "No payment methods added yet"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-bold text-gray-700">Name</TableHead>
                  <TableHead className="font-bold text-gray-700 text-center">Status</TableHead>
                  <TableHead className="font-bold text-gray-700 text-center">Enabled</TableHead>
                  <TableHead className="font-bold text-gray-700 text-center">Active</TableHead>
                  <TableHead className="font-bold text-gray-700 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMethods.map((method) => (
                  <TableRow key={method.id} className="hover:bg-gray-50" data-testid={`payment-method-row-${method.id}`}>
                    <TableCell className="font-medium text-gray-900">{method.name}</TableCell>
                    <TableCell className="text-center">
                      {method.is_enabled && method.is_active ? (
                        <Badge className="bg-green-100 text-green-700">Available</Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-gray-100 text-gray-600">Unavailable</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch
                        checked={method.is_enabled}
                        onCheckedChange={() => handleToggleEnabled(method)}
                        data-testid={`toggle-enabled-${method.id}`}
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch
                        checked={method.is_active}
                        onCheckedChange={() => handleToggleActive(method)}
                        data-testid={`toggle-active-${method.id}`}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(method)}
                          className="text-gray-600 hover:text-[#0A4CAE]"
                          data-testid={`edit-payment-method-${method.id}`}
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteDialog(method)}
                          className="text-gray-600 hover:text-red-500"
                          data-testid={`delete-payment-method-${method.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900">
              {selectedMethod ? "Edit Payment Method" : "Add Payment Method"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="name" className="text-gray-700 font-medium">Name</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="e.g., Cash on Delivery, UPI, Card"
                className="mt-1 bg-white border-gray-300"
                data-testid="input-payment-method-name"
              />
            </div>
            <div className="flex items-center justify-between py-2">
              <Label htmlFor="is_enabled" className="text-gray-700 font-medium">Enabled</Label>
              <Switch
                id="is_enabled"
                checked={formData.is_enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, is_enabled: checked })}
                data-testid="input-payment-method-enabled"
              />
            </div>
            <div className="flex items-center justify-between py-2">
              <Label htmlFor="is_active" className="text-gray-700 font-medium">Active</Label>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                data-testid="input-payment-method-active"
              />
            </div>
            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="border-gray-300 text-gray-700"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#0A4CAE] text-white hover:bg-[#F7851E]"
                data-testid="submit-payment-method-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {selectedMethod ? "Updating..." : "Adding..."}
                  </>
                ) : (
                  selectedMethod ? "Update" : "Add Payment Method"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-white sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900">Delete Payment Method</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600 mt-2">
            Are you sure you want to delete <span className="font-semibold">{selectedMethod?.name}</span>? 
            This action cannot be undone.
          </p>
          <DialogFooter className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="border-gray-300 text-gray-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={submitting}
              className="bg-red-500 text-white hover:bg-red-600"
              data-testid="confirm-delete-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentMethodsPage;
